import React, { useState, useEffect } from "react";
import "./Rooms.css";
import { Link } from "react-router-dom";

import vinilImg from "../../assets/img/radiola.rm.jpg";
import radiolaImg from "../../assets/img/vinil.rm.jpg";
import discoImg from "../../assets/img/discopolaroid.rm.jpg";
import livrmImg from "../../assets/img/mg.rm.jpg";
import bgImg from "../../assets/img/bg_card1.jpg";
import residenceImg from "../../assets/img/residence_card1.jpg";

import { useSelector } from "react-redux";
import { translations, normalizeLang } from "../../i18n";

const loadedRoomsImgCache = new Map();

function RoomsCachedImg({
  src,
  alt = "",
  className = "rooms__img",
  fit = "cover",
  wrapperClass = "rooms__img-wrapper",
}) {
  const isCached = loadedRoomsImgCache.get(src)?.status === "loaded";
  const [fadeIn, setFadeIn] = useState(isCached);
  const [showSkeleton, setShowSkeleton] = useState(!isCached);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById("pli-shimmer-keyframes")) return;
    const styleEl = document.createElement("style");
    styleEl.id = "pli-shimmer-keyframes";
    styleEl.textContent = `
      @keyframes pli-shimmer { 100% { transform: translateX(100%); } }
      @keyframes pli-fade { from { opacity:0; } to { opacity:1; } }
    `;
    document.head.appendChild(styleEl);
  }, []);

  useEffect(() => {
    if (isCached) {
      setShowSkeleton(false);
      setFadeIn(true);
      return;
    }
    setFadeIn(false);
    setShowSkeleton(true);
    let cancelled = false;
    const img = new window.Image();
    img.src = src;
    img.onload = async () => {
      try {
        if (img.decode) await img.decode();
      } catch {}
      loadedRoomsImgCache.set(src, { status: "loaded" });
      if (cancelled) return;
      setTimeout(() => {
        setShowSkeleton(false);
        setFadeIn(true);
      }, 500); // задержка 1 секунда
    };
    img.onerror = () => {
      loadedRoomsImgCache.set(src, { status: "error" });
      setShowSkeleton(false);
      setFadeIn(true);
    };
    return () => {
      cancelled = true;
    };
  }, [src, isCached]);

  return (
    <div
      className={wrapperClass}
      style={{
        borderRadius: 12,
        aspectRatio: "700 / 475",
        overflow: "hidden",
        position: "relative",
        width: "100%",
        height: "100%",
        background: showSkeleton ? "#f2f2f2" : "transparent",
      }}
    >
      {showSkeleton && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "#E0E0E0",
            animation: "pli-fade 0.18s ease-out both",
            zIndex: 2,
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              transform: "translateX(-100%)",
              background:
                "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.66) 50%, rgba(255,255,255,0) 100%)",
              animation: "pli-shimmer 1.2s infinite",
            }}
          />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={className}
        style={{
          objectFit: fit,
          opacity: fadeIn ? 1 : 0,
          transition: "opacity 0.45s cubic-bezier(.42,0,.58,1.0)",
          borderRadius: 12,
          width: "100%",
          height: "100%",
          display: "block",
          zIndex: 1,
          background: "transparent",
        }}
        loading="eager"
        decoding="async"
      />
      {fadeIn && (
        <div
          className="rooms__img-overlay"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 12,
            zIndex: 3,
          }}
        />
      )}
    </div>
  );
}

const Rooms = () => {
  const [openId, setOpenId] = useState(null);

  const toggle = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  const user = useSelector((state) => state.user.data);

  const lang = normalizeLang(user?.languageCode);
  const t = translations[lang];
  return (
    <div className="rooms__wrapper">
      <div style={{ fontSize: "12px", marginBottom: "15px", display: "flex" }}>
        <svg
          style={{ marginRight: "5px" }}
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.5 0C3.35775 0 0 3.35775 0 7.5C0 11.6422 3.35775 15 7.5 15C11.6422 15 15 11.6422 15 7.5C15 3.35775 11.6422 0 7.5 0ZM7.5 1.5C8.49 1.5 9.5775 3.77401 9.74325 6.74776L5.26575 6.75675C5.43075 3.783 6.51 1.5 7.5 1.5ZM4.85175 2.10226C4.20675 3.35701 3.86175 5.02051 3.7785 6.75151L1.54351 6.74476C1.76701 4.70101 3.14325 2.98651 4.85175 2.10226ZM10.152 2.10601C11.8605 2.99026 13.2015 4.65675 13.4602 6.75675L11.226 6.75C11.1555 4.95 10.7573 3.36751 10.152 2.10601ZM1.548 8.253L3.78225 8.23425C3.8655 9.96525 4.21274 11.6572 4.85474 12.8985C3.06074 11.9992 1.806 10.239 1.548 8.253ZM5.25375 8.259L9.73125 8.24924C9.56625 11.2222 8.49 13.5 7.5 13.5C6.51 13.5 5.41875 11.2327 5.25375 8.259ZM11.2297 8.25151L13.4542 8.24476C13.1962 10.2315 11.9423 12.006 10.149 12.8903C10.815 11.5133 11.1465 9.98251 11.2297 8.25151Z"
            fill="black"
          />
        </svg>
        <p>{t.headerTitle}</p>
      </div>

      <p style={{ marginBottom: "10px", fontSize: "14px" }}>
        <b>{t.sacredRoomsTitle}</b>
      </p>

      <div className="artef__wrapper" style={{ padding: "0" }}>
        <Link to={`/rooms/vinil`} className="artef__item">
          <svg
            className="shop__svg"
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="12.5"
              width="17.6777"
              height="17.6777"
              rx="8.83883"
              transform="rotate(45 12.5 0)"
              fill="black"
              fillOpacity="0.4"
            />
            <path
              d="M11.5423 9.62565L15.3757 9.62565M15.3757 9.62565L15.3757 13.459M15.3757 9.62565L9.62565 15.3757"
              stroke="white"
              strokeWidth="0.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <RoomsCachedImg src={vinilImg} alt="vinil" />
          <div className="artef__text">
            <div style={{ fontSize: "12px" }}>{t.vinylRoomName}</div>
            <div
              style={{
                fontSize: "9px",
                opacity: "0.6",
                margin: "3px 0 6px",
              }}
            >
              {t.vinylRoomDesc}
            </div>
            <div
              style={{
                fontSize: "9px",
                backgroundColor: "white",
                color: "black",
                padding: "5px",
                textAlign: "center",
                borderRadius: "8px",
              }}
            >
              {t.details}
            </div>
          </div>
          <div className="shop__img-overlay" style={{ borderRadius: "8px" }} />
        </Link>

        <Link to={`/rooms/radiola`} className="artef__item">
          <svg
            className="shop__svg"
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="12.5"
              width="17.6777"
              height="17.6777"
              rx="8.83883"
              transform="rotate(45 12.5 0)"
              fill="black"
              fillOpacity="0.4"
            />
            <path
              d="M11.5423 9.62565L15.3757 9.62565M15.3757 9.62565L15.3757 13.459M15.3757 9.62565L9.62565 15.3757"
              stroke="white"
              strokeWidth="0.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <RoomsCachedImg src={radiolaImg} alt="radiola" />
          <div className="artef__text">
            <div style={{ fontSize: "12px" }}>{t.radiolaRoomName}</div>
            <div
              style={{
                fontSize: "9px",
                opacity: "0.6",
                margin: "3px 0 6px",
              }}
            >
              {t.radiolaRoomDesc}
            </div>
            <div
              style={{
                fontSize: "9px",
                backgroundColor: "white",
                color: "black",
                padding: "5px",
                textAlign: "center",
                borderRadius: "8px",
              }}
            >
              {t.details}
            </div>
          </div>
          <div className="shop__img-overlay" style={{ borderRadius: "8px" }} />
        </Link>
      </div>

      <p style={{ margin: "10px 0", marginTop: "30px", fontSize: "14px" }}>
        <b>{t.rentSpacesTitle}</b>
      </p>

      <div className="artef__wrapper" style={{ padding: "0" }}>
        {/* <Link to={`/rooms/livingrm`} className="artef__item"> */}
        <div className="artef__item">
          <div className="dark-overlay"></div>
          {/* <svg
            className="shop__svg"
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="12.5"
              width="17.6777"
              height="17.6777"
              rx="8.83883"
              transform="rotate(45 12.5 0)"
              fill="black"
              fillOpacity="0.4"
            />
            <path
              d="M11.5423 9.62565L15.3757 9.62565M15.3757 9.62565L15.3757 13.459M15.3757 9.62565L9.62565 15.3757"
              stroke="white"
              strokeWidth="0.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg> */}
          <RoomsCachedImg src={livrmImg} alt="vinil" />
          <div className="artef__text">
            <div style={{ fontSize: "12px" }}>{t.smallLivingRoomName}</div>
            <div
              style={{
                fontSize: "9px",
                opacity: "0.6",
                margin: "3px 0 6px",
              }}
            >
              {t.smallLivingRoomDesc}
            </div>
            <div
              style={{
                fontSize: "9px",
                backgroundColor: "white",
                color: "black",
                padding: "5px",
                textAlign: "center",
                borderRadius: "8px",
              }}
            >
              Coming soon
            </div>
          </div>
          <div className="shop__img-overlay" style={{ borderRadius: "8px" }} />
        </div>
        {/* </Link> */}

        {/* <Link to={`/rooms/disco`} className="artef__item"> */}
        <div className="artef__item">
          <div className="dark-overlay"></div>
          {/* <svg
            className="shop__svg"
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="12.5"
              width="17.6777"
              height="17.6777"
              rx="8.83883"
              transform="rotate(45 12.5 0)"
              fill="black"
              fillOpacity="0.4"
            />
            <path
              d="M11.5423 9.62565L15.3757 9.62565M15.3757 9.62565L15.3757 13.459M15.3757 9.62565L9.62565 15.3757"
              stroke="white"
              strokeWidth="0.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg> */}
          <RoomsCachedImg src={discoImg} alt="radiola" />
          <div className="artef__text">
            <div style={{ fontSize: "12px" }}>{t.discoHallName}</div>
            <div
              style={{
                fontSize: "9px",
                opacity: "0.6",
                margin: "3px 0 6px",
              }}
            >
              {t.discoHallDesc}
            </div>
            <div
              style={{
                fontSize: "9px",
                backgroundColor: "white",
                color: "black",
                padding: "5px",
                textAlign: "center",
                borderRadius: "8px",
              }}
            >
              Coming soon
            </div>
          </div>
          <div className="shop__img-overlay" style={{ borderRadius: "8px" }} />
        </div>
        {/* </Link> */}

        {/* <Link to={`/rooms/bigliving`} className="artef__item"> */}
        <div className="artef__item">
          <div className="dark-overlay"></div>
          {/* <svg
            className="shop__svg"
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="12.5"
              width="17.6777"
              height="17.6777"
              rx="8.83883"
              transform="rotate(45 12.5 0)"
              fill="black"
              fillOpacity="0.4"
            />
            <path
              d="M11.5423 9.62565L15.3757 9.62565M15.3757 9.62565L15.3757 13.459M15.3757 9.62565L9.62565 15.3757"
              stroke="white"
              strokeWidth="0.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg> */}
          <RoomsCachedImg src={bgImg} alt="bigliving" />
          <div className="artef__text">
            <div style={{ fontSize: "12px" }}>{t.bigLivingRoomName}</div>
            <div
              style={{
                fontSize: "9px",
                opacity: "0.6",
                margin: "3px 0 6px",
              }}
            >
              {t.bigLivingRoomDesc}
            </div>
            <div
              style={{
                fontSize: "9px",
                backgroundColor: "white",
                color: "black",
                padding: "5px",
                textAlign: "center",
                borderRadius: "8px",
              }}
            >
              Cooming soon
            </div>
          </div>
          <div className="shop__img-overlay" style={{ borderRadius: "8px" }} />
        </div>
        {/* </Link> */}

        {/* <Link to={`/rooms/residence`} className="artef__item"> */}
        <div className="artef__item">
          <div className="dark-overlay"></div>
          {/* <svg
            className="shop__svg"
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="12.5"
              width="17.6777"
              height="17.6777"
              rx="8.83883"
              transform="rotate(45 12.5 0)"
              fill="black"
              fillOpacity="0.4"
            />
            <path
              d="M11.5423 9.62565L15.3757 9.62565M15.3757 9.62565L15.3757 13.459M15.3757 9.62565L9.62565 15.3757"
              stroke="white"
              strokeWidth="0.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg> */}
          <RoomsCachedImg src={residenceImg} alt="residence" />
          <div className="artef__text">
            <div style={{ fontSize: "12px" }}>{t.wholeResidenceName}</div>
            <div
              style={{
                fontSize: "9px",
                opacity: "0.6",
                margin: "3px 0 6px",
              }}
            >
              {t.wholeResidenceDesc}
            </div>
            <div
              style={{
                fontSize: "9px",
                backgroundColor: "white",
                color: "black",
                padding: "5px",
                textAlign: "center",
                borderRadius: "8px",
              }}
            >
              Coming soon
            </div>
          </div>
          <div className="shop__img-overlay" style={{ borderRadius: "8px" }} />
        </div>
        {/* </Link> */}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginTop: "20px",
          marginBottom: "15px",
          fontSize: "12px",
        }}
      >
        <svg
          width="17"
          height="17"
          viewBox="0 0 17 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginRight: "5px", flexShrink: 0 }}
        >
          <circle cx="8.5" cy="4.75" r="0.75" fill="black" />
          <path
            d="M7.75 7H8.5V12.25M16 8.5C16 12.6421 12.6421 16 8.5 16C4.35786 16 1 12.6421 1 8.5C1 4.35786 4.35786 1 8.5 1C12.6421 1 16 4.35786 16 8.5Z"
            stroke="black"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <p style={{ margin: 0 }}>{t.faqSecretRoomsTitle}</p>
      </div>

      <div className="infoContent" style={{ marginTop: "0px" }}>
        {t.faqPageRooms.questions.slice(0, 4).map((q) => (
          <div key={q.id} className="questionContainer">
            <div className="questionButton" onClick={() => toggle(q.id)}>
              <p style={{ fontSize: "12px", fontWeight: "400" }}>{q.title}</p>
              <svg
                width="6"
                height="13"
                style={{
                  transform: openId === q.id ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "0.3s ease",
                }}
                viewBox="0 0 6 13"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.307096 0.179318C-0.0457526 0.461597 -0.102961 0.97647 0.179318 1.32932L4.13404 6.27273L0.179318 11.2161C-0.102961 11.569 -0.0457532 12.0839 0.307096 12.3661C0.659945 12.6484 1.17482 12.5912 1.4571 12.2384L5.82071 6.78384C6.05976 6.48502 6.05976 6.06043 5.82071 5.76161L1.4571 0.307096C1.17482 -0.0457526 0.659945 -0.102961 0.307096 0.179318Z"
                  fill="#366091"
                />
              </svg>
            </div>
            <div className={`answer ${openId === q.id ? "open" : ""}`}>
              <p style={{ opacity: "0.8", fontSize: "12px" }}>{q.answer}</p>
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginTop: "20px",
          marginBottom: "15px",
          fontSize: "12px",
        }}
      >
        <svg
          width="17"
          height="17"
          viewBox="0 0 17 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginRight: "5px", flexShrink: 0 }}
        >
          <circle cx="8.5" cy="4.75" r="0.75" fill="black" />
          <path
            d="M7.75 7H8.5V12.25M16 8.5C16 12.6421 12.6421 16 8.5 16C4.35786 16 1 12.6421 1 8.5C1 4.35786 4.35786 1 8.5 1C12.6421 1 16 4.35786 16 8.5Z"
            stroke="black"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <p style={{ margin: 0 }}>{t.faqRentSpacesTitle}</p>
      </div>

      <div className="infoContent" style={{ marginTop: "0px" }}>
        {t.faqPageRooms.questions.slice(4).map((q) => (
          <div key={q.id} className="questionContainer">
            <div className="questionButton" onClick={() => toggle(q.id)}>
              <p style={{ fontSize: "12px", fontWeight: "400" }}>{q.title}</p>
              <svg
                width="6"
                height="13"
                style={{
                  transform: openId === q.id ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "0.3s ease",
                }}
                viewBox="0 0 6 13"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.307096 0.179318C-0.0457526 0.461597 -0.102961 0.97647 0.179318 1.32932L4.13404 6.27273L0.179318 11.2161C-0.102961 11.569 -0.0457532 12.0839 0.307096 12.3661C0.659945 12.6484 1.17482 12.5912 1.4571 12.2384L5.82071 6.78384C6.05976 6.48502 6.05976 6.06043 5.82071 5.76161L1.4571 0.307096C1.17482 -0.0457526 0.659945 -0.102961 0.307096 0.179318Z"
                  fill="#366091"
                />
              </svg>
            </div>
            <div className={`answer ${openId === q.id ? "open" : ""}`}>
              <p style={{ opacity: "0.8", fontSize: "12px" }}>{q.answer}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Rooms;
